import { useRef, useState, useEffect, Fragment } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  Input,
  ErrorMessage,
  Fieldset,
  Paragraph,
  Button,
  Form,
  Layout,
  Spinner,
} from '@vwfs-bronson/bronson-react'
import { useNavigate } from 'react-router-dom'
import { BrFormField } from '../FormField'
import { TanAuthentificationValidationSchema } from '../../services/validation'
import { continueJourney } from '../../services/api/journey/continueJourney'
import { qs } from '../../services/routing'
import { generateTan } from '../../services/api/tan'
import { Analytics, AnalyticsEvent } from '../../services/analytics'

export function TanAuthentification() {
  const transactionId = qs(['id', 'cid'])
  const [tanError, setTanError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()
  const formRef = useRef()
  const navigate = useNavigate()

  useEffect(() => {
      Analytics.track(new AnalyticsEvent.ContinueLaterTanReloaded(transactionId))
      requestTan()
  }, [transactionId, requestTan])

  useEffect(() => {
    if (tanError) {
      Analytics.track(new AnalyticsEvent.ContinueLaterTanSubmitError(transactionId))
    }
  }, [tanError, transactionId])

  const onValidate = (tan, setTanError, navigate) => {
    setIsLoading(true)
    continueJourney(tan, transactionId, navigate)
      .then(
        response => {
          if (!response?.transaction?.status) {
            setTanError(true)
          }
        },
        error => {
          if (error.message.includes('Expired')) {
            navigate('/link-expired')
          } else {
            navigate('/error')
          }
        }
      )
      .catch(() => {
        navigate('/error')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const requestTan = () => {
    setIsLoading(true)
    generateTan(transactionId)
      .then(
        () => null, // SMS sent
        error => {
          if (error.message.includes('Expired')) {
            navigate('/link-expired')
          } else {
            navigate('/error')
          }
        }
      )
      .catch(() => navigate('/error'))
      .finally(() => setIsLoading(false))
  }

  return (
    <Fragment>
      { isLoading && <Spinner fullPage />}
      {transactionId && !isLoading && (
        <Modal
          className="c-modal--stretch"
          testId="tanAuthentificationModal"
          id="tanAuthentificationModal"
          shown
          center
          title={t('tanAuthentification:heading')}
          buttonConfirmText={t('tanAuthentification:buttonText')}
          buttonConfirmType="submit"
          onConfirm={async () => {
            await formRef.current.submitForm()
          }}
          hideCloseButton
        >
          {isLoading && <Spinner fullPage />}
          <Paragraph>{t('tanAuthentification:content:p1')}</Paragraph>
          <Paragraph>{t('tanAuthentification:content:p2')}</Paragraph>
          <Paragraph>{t('tanAuthentification:content:p3')}</Paragraph>
          {tanError && (
            <ErrorMessage>
              {t('tanAuthentification:inlineErrorMessage')}
            </ErrorMessage>
          )}
          <Layout center className="u-mb-small">
            <Layout.Item default="1/2" s="1/1">
              <Formik
                initialValues={{ tanAuthentification: '' }}
                validationSchema={TanAuthentificationValidationSchema}
                onSubmit={values =>
                  onValidate(
                    values.tanAuthentification,
                    setTanError,
                    navigate
                  )
                }
              >
                {formikProps => {
                  formRef.current = formikProps
                  return (
                    <Form floatingLabel onSubmit={formikProps.handleSubmit}>
                      <Fieldset>
                        <Fieldset.Row>
                          <BrFormField
                            name="tanAuthentification"
                            render={fieldProps => <Input {...fieldProps} />}
                          />
                        </Fieldset.Row>
                      </Fieldset>
                    </Form>
                  )
                }}
              </Formik>
            </Layout.Item>
          </Layout>
          <Button
            link
            icon="semantic-forward"
            type="button"
            onClick={() => {
              Analytics.track(new AnalyticsEvent.ContinueLaterTanRequest(transactionId))
              requestTan(transactionId, navigate)
            }}
          >
            {t('tanAuthentification:requestTanLink')}
          </Button>
        </Modal>
      )}
    </Fragment>
  )
}
